import { useState, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { Button, Loader, DialogFooter, MonthYearPicker } from "@/ui/atoms";
import { MultiSelect } from "@/ui/molecules/MultiSelect";
import { getCurrentPeriod } from "@/utils/utils";
import { FileUploadZone } from "../FileUploadZone";
import { Upload, X } from "lucide-react";

type TDealOption = {
    id: string;
    name: string;
    code: string;
    isLive?: boolean;
    acceptsOtb?: boolean;
    dealFileType?: "tb_only" | "tb_and_gl";
};

type TApiFileType =
    | "trial_balance"
    | "general_ledger"
    | "open_trial_balance"
    | "financial_journals"
    | "tb_gl";

const fileTypeLabels: Record<TApiFileType, string> = {
    trial_balance: "TB",
    general_ledger: "GL",
    open_trial_balance: "OTB",
    financial_journals: "Financial Journals",
    tb_gl: "TBGL",
};

export interface TFileUploadFormProps {
    /**
     * Preselected file type (used when component is for a specific file type)
     */
    preselectedFileType?: TApiFileType;
    /**
     * Preselected deal (Homepage v2 "Upload now" deep link). Safe to pass a
     * value not present in `deals` — the select simply shows no option
     * selected until it matches one, rather than erroring.
     */
    preselectedDealId?: string;
    /**
     * Preselected period, "YYYY-MM" (Homepage v2 deep link). Falls back to
     * the current period when omitted, same as before this prop existed.
     */
    preselectedPeriod?: string;
    /**
     * If provided, only these file types will be shown in the dropdown
     */
    allowedFileTypes?: TApiFileType[];
    /**
     * If true, the file type selector is disabled (locked to preselectedFileType)
     */
    lockedFileType?: boolean;
    /**
     * Called when file is successfully uploaded
     */
    onSuccess?: () => void;
    /**
     * Deals for the dropdown
     */
    deals: TDealOption[];
    /**
     * Callback to upload the file
     */
    onUploadFile: (data: {
        dealId: string;
        period: string;
        fileType: string;
        file: File;
        sheetName?: string;
        tbSheetName?: string;
        glSheetName?: string;
        contributorIds?: string[];
    }) => Promise<void>;
    /**
     * Show cancel button in footer
     */
    showCancel?: boolean;
    /**
     * Callback when cancel is clicked
     */
    onCancel?: () => void;
    /**
     * Eligible FinJournal contributor options ({ id, label }). Only rendered when
     * the file type is Financial Journals.
     */
    contributorOptions?: { id: string; label: string }[];
    /**
     * Whether the contributor options are still loading.
     */
    contributorsLoading?: boolean;
    /**
     * Called when the selected deal changes, so the parent can (re)fetch the
     * role-scoped contributor candidates for that deal.
     */
    onDealChange?: (dealId: string) => void;
}

function readExcelSheetNames(file: File): Promise<string[]> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target?.result;
                const wb = XLSX.read(data, { type: "array", bookSheets: true });
                resolve(wb.SheetNames);
            } catch {
                resolve([]);
            }
        };
        reader.onerror = () => resolve([]);
        reader.readAsArrayBuffer(file);
    });
}

function isExcelFile(file: File): boolean {
    return (
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls") ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel"
    );
}

export function FileUploadForm({
    preselectedFileType = "trial_balance",
    preselectedDealId = "",
    preselectedPeriod,
    allowedFileTypes,
    lockedFileType = false,
    onSuccess,
    deals,
    onUploadFile,
    showCancel = true,
    onCancel,
    contributorOptions = [],
    contributorsLoading = false,
    onDealChange,
}: TFileUploadFormProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [availableSheets, setAvailableSheets] = useState<string[]>([]);
    const [contributorIds, setContributorIds] = useState<string[]>([]);
    const [uploadForm, setUploadForm] = useState({
        dealId: preselectedDealId,
        period: preselectedPeriod || getCurrentPeriod(),
        fileType: preselectedFileType,
        sheetName: "",
        tbSheetName: "",
        glSheetName: "",
    });

    // Show deals alphabetically by name in the dropdown.
    const sortedDeals = useMemo(
        () =>
            [...deals].sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
            ),
        [deals],
    );

    // Check if selected deal requires OTB commit before TB/GL uploads. Mirrors the
    // backend's OTB_GATED_FILE_TYPES: only the types that build on the opening
    // position are held back, so FinJournals (and the OTB itself) stay uploadable.
    const otbGatedFileTypes: TApiFileType[] = ["trial_balance", "general_ledger", "tb_gl"];
    const selectedDeal = deals.find((d) => d.id === uploadForm.dealId);
    const isDealGated =
        otbGatedFileTypes.includes(uploadForm.fileType) &&
        selectedDeal?.acceptsOtb === true &&
        selectedDeal?.isLive !== true;
    // TB-Only deals accept neither standalone GL nor combined TB+GL uploads.
    const isGlBlocked = selectedDeal?.dealFileType === "tb_only";
    const blockedFileTypes: TApiFileType[] = isGlBlocked ? ["general_ledger", "tb_gl"] : [];

    // Determine which file types to show
    const availableFileTypes: TApiFileType[] = allowedFileTypes || [
        "trial_balance",
        "general_ledger",
        "open_trial_balance",
        "financial_journals",
        "tb_gl",
    ];

    // When a file is selected, read its sheet names
    useEffect(() => {
        if (!selectedFile || !isExcelFile(selectedFile)) {
            setAvailableSheets([]);
            setUploadForm((prev) => ({ ...prev, sheetName: "", tbSheetName: "", glSheetName: "" }));
            return;
        }

        readExcelSheetNames(selectedFile).then((sheets) => {
            setAvailableSheets(sheets);
            if (sheets.length > 0) {
                if (uploadForm.fileType === "tb_gl") {
                    const tbDefault = sheets.find((s) => s === "TB") ?? sheets[0] ?? "";
                    const glDefault =
                        sheets.find((s) => s === "GL") ??
                        sheets[Math.min(1, sheets.length - 1)] ??
                        "";
                    setUploadForm((prev) => ({
                        ...prev,
                        tbSheetName: tbDefault,
                        glSheetName: glDefault,
                    }));
                } else {
                    setUploadForm((prev) => ({ ...prev, sheetName: sheets[0] ?? "" }));
                }
            }
        });
    }, [selectedFile]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset sheet selections when file type changes
    useEffect(() => {
        if (availableSheets.length === 0) return;
        if (uploadForm.fileType === "tb_gl") {
            const tbDefault = availableSheets.find((s) => s === "TB") ?? availableSheets[0] ?? "";
            const glDefault =
                availableSheets.find((s) => s === "GL") ??
                availableSheets[Math.min(1, availableSheets.length - 1)] ??
                "";
            setUploadForm((prev) => ({
                ...prev,
                tbSheetName: tbDefault,
                glSheetName: glDefault,
                sheetName: "",
            }));
        } else {
            setUploadForm((prev) => ({
                ...prev,
                sheetName: availableSheets[0] ?? "",
                tbSheetName: "",
                glSheetName: "",
            }));
        }
    }, [uploadForm.fileType]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset to TB when the selected deal blocks the currently selected file type
    useEffect(() => {
        if (blockedFileTypes.includes(uploadForm.fileType)) {
            setUploadForm((prev) => ({ ...prev, fileType: "trial_balance" }));
        }
    }, [isGlBlocked]); // eslint-disable-line react-hooks/exhaustive-deps

    // Contributors are FinJournal-only; keep the field hidden for TB/GL/OTB/TBGL.
    const showContributors = uploadForm.fileType === "financial_journals";

    // When the deal changes, clear the selected contributors (a candidate valid for
    // one deal may not be for another) and let the parent refetch the candidate list.
    useEffect(() => {
        setContributorIds([]);
        onDealChange?.(uploadForm.dealId);
    }, [uploadForm.dealId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
    };

    const handleReset = () => {
        setSelectedFile(null);
        setAvailableSheets([]);
        setUploadError(null);
        setContributorIds([]);
        setUploadForm({
            dealId: "",
            period: getCurrentPeriod(),
            fileType: preselectedFileType,
            sheetName: "",
            tbSheetName: "",
            glSheetName: "",
        });
    };

    const handleUploadSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile || !uploadForm.dealId) return;

        setIsUploading(true);
        setUploadError(null);
        try {
            // Convert period from YYYY-MM to YYYY-MM-DD format for the API
            const periodDate = `${uploadForm.period}-01`;
            await onUploadFile({
                dealId: uploadForm.dealId,
                period: periodDate,
                fileType: uploadForm.fileType,
                file: selectedFile,
                sheetName: uploadForm.sheetName || undefined,
                tbSheetName: uploadForm.tbSheetName || undefined,
                glSheetName: uploadForm.glSheetName || undefined,
                contributorIds: showContributors ? contributorIds : undefined,
            });
            handleReset();
            onSuccess?.();
        } catch (error) {
            console.error("Upload failed:", error);
            const errorMessage =
                error instanceof Error ? error.message : "Upload failed. Please try again.";
            setUploadError(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    const showSheetSelector = availableSheets.length > 0;

    return (
        <form onSubmit={handleUploadSubmit} className="space-y-4">
            <div>
                <label htmlFor="deal" className="block text-sm font-medium mb-1">
                    Deal <span className="text-destructive">*</span>
                </label>
                <select
                    id="deal"
                    value={uploadForm.dealId}
                    onChange={(e) => setUploadForm({ ...uploadForm, dealId: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                >
                    <option value="">Select a deal...</option>
                    {sortedDeals.map((deal) => (
                        <option key={deal.id} value={deal.id}>
                            {deal.name}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <span className="block text-sm font-medium mb-1">
                    File Type <span className="text-destructive">*</span>
                </span>
                <div className="flex flex-col gap-2">
                    {availableFileTypes.map((type) => {
                        const blocked = blockedFileTypes.includes(type);
                        return (
                            <label
                                key={type}
                                title={
                                    blocked
                                        ? "This deal is Trial Balance only. GL and TBGL files can't be uploaded to it."
                                        : undefined
                                }
                                className={`flex items-center gap-2 ${
                                    blocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                }`}
                            >
                                <input
                                    type="radio"
                                    name="fileType"
                                    value={type}
                                    checked={uploadForm.fileType === type}
                                    onChange={() =>
                                        setUploadForm({
                                            ...uploadForm,
                                            fileType: type,
                                        })
                                    }
                                    disabled={lockedFileType || blocked}
                                    required
                                    className="accent-primary"
                                />
                                <span className="text-sm">{fileTypeLabels[type]}</span>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div>
                <label
                    id="file-upload-form-period-label"
                    htmlFor="period"
                    className="block text-sm font-medium mb-1"
                >
                    Period <span className="text-destructive">*</span>
                </label>
                <MonthYearPicker
                    id="period"
                    ariaLabelledBy="file-upload-form-period-label"
                    value={uploadForm.period}
                    onChange={(period) => setUploadForm({ ...uploadForm, period })}
                    required
                    inline
                    className="w-1/2 max-w-full min-w-0"
                />
            </div>

            <div>
                <label htmlFor="file" className="block text-sm font-medium mb-1">
                    File <span className="text-destructive">*</span>
                </label>
                {selectedFile ? (
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                        <span className="text-sm truncate">{selectedFile.name}</span>
                        <button
                            type="button"
                            onClick={() => setSelectedFile(null)}
                            className="p-1 hover:bg-muted rounded"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                ) : (
                    <FileUploadZone
                        id="file"
                        onFileSelect={handleFileSelect}
                        accept={{
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
                                ".xlsx",
                            ],
                            "application/vnd.ms-excel": [".xls"],
                            "text/csv": [".csv"],
                        }}
                        className="min-h-[120px]"
                    />
                )}
            </div>

            {showSheetSelector &&
                (uploadForm.fileType === "tb_gl" ? (
                    <>
                        <div>
                            <label
                                htmlFor="tb-sheet-name"
                                className="block text-sm font-medium mb-1"
                            >
                                TB Sheet
                            </label>
                            <select
                                id="tb-sheet-name"
                                value={uploadForm.tbSheetName}
                                onChange={(e) =>
                                    setUploadForm({ ...uploadForm, tbSheetName: e.target.value })
                                }
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            >
                                {availableSheets.map((sheet) => (
                                    <option key={sheet} value={sheet}>
                                        {sheet}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label
                                htmlFor="gl-sheet-name"
                                className="block text-sm font-medium mb-1"
                            >
                                GL Sheet
                            </label>
                            <select
                                id="gl-sheet-name"
                                value={uploadForm.glSheetName}
                                onChange={(e) =>
                                    setUploadForm({ ...uploadForm, glSheetName: e.target.value })
                                }
                                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                            >
                                {availableSheets.map((sheet) => (
                                    <option key={sheet} value={sheet}>
                                        {sheet}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </>
                ) : (
                    <div>
                        <label htmlFor="sheet-name" className="block text-sm font-medium mb-1">
                            Sheet
                        </label>
                        <select
                            id="sheet-name"
                            value={uploadForm.sheetName}
                            onChange={(e) =>
                                setUploadForm({ ...uploadForm, sheetName: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        >
                            {availableSheets.map((sheet) => (
                                <option key={sheet} value={sheet}>
                                    {sheet}
                                </option>
                            ))}
                        </select>
                    </div>
                ))}

            {showContributors && (
                <div className="flex items-center gap-3">
                    <label htmlFor="contributors" className="text-sm font-medium w-28 shrink-0">
                        Contributors
                    </label>
                    <div className="min-w-0 flex-1">
                        <MultiSelect
                            options={contributorOptions}
                            selectedIds={contributorIds}
                            onChange={setContributorIds}
                            placeholder="Select contributors..."
                            emptyMessage="No contributors available"
                            disabled={contributorsLoading || !uploadForm.dealId}
                        />
                    </div>
                </div>
            )}

            {isDealGated && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700">
                    This deal requires an OTB to be committed before TB/GL files can be uploaded.
                </div>
            )}

            {uploadError && (
                <div
                    data-testid="upload-error"
                    className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
                >
                    {uploadError}
                </div>
            )}

            <DialogFooter>
                {showCancel && (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                            handleReset();
                            onCancel?.();
                        }}
                        disabled={isUploading}
                    >
                        Cancel
                    </Button>
                )}
                <Button
                    type="submit"
                    disabled={isUploading || !selectedFile || !uploadForm.dealId || isDealGated}
                >
                    {isUploading ? (
                        <>
                            <Loader size="sm" className="mr-2" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <Upload className="mr-2 h-4 w-4" />
                            Upload
                        </>
                    )}
                </Button>
            </DialogFooter>
        </form>
    );
}
